/**
 * @file cabinet-palette.ts
 * @description Палитра темы кабинета (phase19.31, SC1–SC3, SC10, SC12, SC27).
 *
 * Тема кабинета = неизменные правила (`styles/console-rules.css`) + палитра
 * цветов шаблона (`@chaiberi/shared`: обязательные ключи + необязательные,
 * напр. `gradient-warm` — тёплый тон фона, SC66). Палитра ставится на <html>
 * отдельным `<style id="cabinet-palette">` с CSS-переменными, плюс атрибуты:
 *  - `data-ui="console"` — правила кабинетов включены;
 *  - `data-scheme="light" | "dark"` — набор цветов статусов (SC27);
 *  - `data-palette="<ключ шаблона>"` — какая палитра стоит (для проверок).
 *
 * Кабинет платформы и вход — всегда «Commora» из пакета, без запроса (SC10).
 * Кабинет магазина — палитра темы магазина: ссылка приходит в `/auth/me`
 * (`shop.cabinetTheme`), цвета — `GET /cabinet-themes/:id/palette`. Ответ
 * кэшируется в localStorage по slug магазина и `updatedAt` темы: при
 * перезагрузке палитра ставится из кэша до первого кадра (без мигания).
 * Не удалось получить палитру — «Commora» из пакета.
 *
 * Значения палитры попадают в CSS, поэтому каждое проверяется по формату
 * `#rrggbb` / `#rrggbbaa` — ничего, кроме цвета, сюда не пройдёт.
 */

import {
  CABINET_COLOR_PATTERN,
  CABINET_FALLBACK_PRESET,
  CABINET_PALETTE_KEYS_V1,
  isCabinetOptionalPaletteKey,
  type CabinetPalette,
  type CabinetThemeScheme,
} from "@chaiberi/shared";

import type { CabinetThemeRef } from "../auth/types";

/** Палитра, которую можно поставить на страницу. */
export interface AppliedPalette {
  /** Ключ шаблона (`commora`, `nature`…) — в атрибут `data-palette`. */
  key: string;
  scheme: CabinetThemeScheme;
  tokens: CabinetPalette;
}

/** Запись кэша: палитра + чем она была на сервере. */
export interface CachedPalette extends AppliedPalette {
  themeId: string;
  updatedAt: string;
}

/** Ответ `GET /cabinet-themes/:id/palette` (cabinet-api-contract.md §3). */
export interface CabinetPaletteDto {
  id: string;
  key: string;
  scheme: CabinetThemeScheme;
  tokensSchema: number;
  tokens: unknown;
  updatedAt: string;
  /** `true` — палитра в базе не прошла проверку, сервер отдал «Commora». */
  fallback: boolean;
}

/** «Commora» из пакета — кабинет платформы, вход и запасная тема. */
export const COMMORA_PALETTE: AppliedPalette = {
  key: CABINET_FALLBACK_PRESET.key,
  scheme: CABINET_FALLBACK_PRESET.scheme,
  tokens: CABINET_FALLBACK_PRESET.tokens,
};

const STYLE_ID = "cabinet-palette";
const CACHE_PREFIX = "commora.cabinetPalette:";

/** Значение — цвет палитры в формате hex. */
function isPaletteColor(value: unknown): value is string {
  return typeof value === "string" && CABINET_COLOR_PATTERN.test(value);
}

/**
 * Палитра годится для кабинета: каждый обязательный ключ — цвет hex;
 * необязательный (`gradient-warm`, SC66) может отсутствовать, но если есть —
 * тоже обязан быть цветом (иначе через него прошёл бы произвольный CSS).
 * Кэш палитры, записанный до появления необязательных ключей, остаётся валидным.
 */
export function isCompletePalette(tokens: unknown): tokens is CabinetPalette {
  if (typeof tokens !== "object" || tokens === null || Array.isArray(tokens)) return false;
  const record = tokens as Record<string, unknown>;
  return CABINET_PALETTE_KEYS_V1.every((key) => {
    const value = record[key];
    if (value === undefined && isCabinetOptionalPaletteKey(key)) return true;
    return isPaletteColor(value);
  });
}

/**
 * CSS палитры: переменные под правилами кабинетов. Необязательный ключ без
 * значения переменную не задаёт — правила берут запасное значение
 * (`var(--gradient-warm, var(--gradient-end))`).
 */
export function paletteCss(tokens: CabinetPalette): string {
  const record: Readonly<Record<string, string | undefined>> = tokens;
  const vars = CABINET_PALETTE_KEYS_V1.map((key) => {
    const value = record[key];
    return value === undefined ? "" : `--${key}:${value};`;
  }).join("");
  return `:root[data-ui="console"]{${vars}}`;
}

/** Поставить палитру на страницу (повторный вызов с той же палитрой — без изменений DOM). */
export function applyPalette(palette: AppliedPalette, doc: Document = document): void {
  const root = doc.documentElement;
  root.setAttribute("data-ui", "console");
  root.setAttribute("data-scheme", palette.scheme === "DARK" ? "dark" : "light");
  root.setAttribute("data-palette", palette.key);
  let style = doc.getElementById(STYLE_ID);
  if (!style) {
    style = doc.createElement("style");
    style.id = STYLE_ID;
    doc.head.appendChild(style);
  }
  const css = paletteCss(palette.tokens);
  if (style.textContent !== css) style.textContent = css;
}

/** Палитра «Commora» (кабинет платформы, вход, запасной вариант). */
export function applyCommoraPalette(doc: Document = document): void {
  applyPalette(COMMORA_PALETTE, doc);
}

/** Палитра из кэша магазина или `null` (нет, повреждена, недоступно хранилище). */
export function readCachedPalette(slug: string): CachedPalette | null {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + slug);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<CachedPalette>;
    if (
      typeof value.themeId !== "string" ||
      typeof value.updatedAt !== "string" ||
      typeof value.key !== "string" ||
      (value.scheme !== "LIGHT" && value.scheme !== "DARK") ||
      !isCompletePalette(value.tokens)
    ) {
      return null;
    }
    return value as CachedPalette;
  } catch {
    return null;
  }
}

/** Запомнить палитру магазина (ошибку хранилища молча пропускаем — кэш необязателен). */
export function writeCachedPalette(slug: string, entry: CachedPalette): void {
  try {
    window.localStorage.setItem(CACHE_PREFIX + slug, JSON.stringify(entry));
  } catch {
    // Хранилище переполнено или запрещено — палитра просто будет запрошена снова.
  }
}

/** Кэш соответствует теме магазина из `/auth/me` (та же тема и та же правка). */
export function isCacheFresh(cached: CachedPalette | null, ref: CabinetThemeRef): cached is CachedPalette {
  return !!cached && cached.themeId === ref.id && cached.updatedAt === ref.updatedAt;
}

/**
 * Палитра темы магазина: свежий кэш — без запроса; иначе запрос к серверу
 * (`fetchPalette`), ответ в кэш (кроме запасного «Commora» от сервера);
 * ошибка или битый ответ — «Commora» из пакета.
 */
export async function resolveShopPalette(
  slug: string,
  ref: CabinetThemeRef,
  fetchPalette: (themeId: string) => Promise<CabinetPaletteDto>,
): Promise<AppliedPalette> {
  const cached = readCachedPalette(slug);
  if (isCacheFresh(cached, ref)) return cached;
  try {
    const dto = await fetchPalette(ref.id);
    if (!isCompletePalette(dto.tokens)) return COMMORA_PALETTE;
    const entry: CachedPalette = {
      key: dto.key,
      scheme: dto.scheme,
      tokens: dto.tokens,
      themeId: ref.id,
      updatedAt: ref.updatedAt,
    };
    if (!dto.fallback) writeCachedPalette(slug, entry);
    return entry;
  } catch {
    return COMMORA_PALETTE;
  }
}

/** slug магазина из пути страницы (`/shop/<slug>/…`) или `null`. */
export function shopSlugFromPath(path: string): string | null {
  const match = /^\/shop\/([^/]+)/.exec(path);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Палитра до первого кадра по адресу: магазин — из кэша (если есть),
 * всё остальное — «Commora». Свежесть кэша сверит каркас магазина.
 */
export function bootPaletteForPath(path: string): AppliedPalette {
  const slug = shopSlugFromPath(path);
  return (slug && readCachedPalette(slug)) || COMMORA_PALETTE;
}
